import React, { useEffect, useRef } from 'react'
import PropTypes from 'prop-types'
import { motion, AnimatePresence, useViewportScroll } from 'framer-motion'
import { disableBodyScroll, clearAllBodyScrollLocks } from 'body-scroll-lock'
import { window } from 'browser-monads'
import Button from './button'
import useScrollDirection from '../hooks/useScrollDirection'
import useWindowSize from '../hooks/useWindowSize'
import Logo from '../images/logo-white.inline.svg'
import Pattern from '../images/tuya-pattern-menu.inline.svg'
import CloseIcon from '../images/close-icon.inline.svg'
import IconDomo from '../images/icon-domo.inline.svg'
import IconCastle from '../images/icon-castle.inline.svg'
import IconGaleria from '../images/icon-galeria.inline.svg'
import IconTeatro from '../images/icon-teatro.inline.svg'

import '../styles/components/header.scss'
import { useState } from 'react'
import { useI18next, Trans, Link } from 'gatsby-plugin-react-i18next'
import LanguageSwitcher from './language-switcher'

const navMenu = [
  { title: 'nav_home', path: '/', class: '' },
  { title: 'nav_food', path: '/food/', class: '' },
  { title: 'nav_bar', path: '/bar/', class: '' },
  { title: 'nav_winecellar', path: '/winecellar/', class: '' },
  { title: 'nav_concept', path: '/concept/', class: '' },
  { title: 'nav_touch', path: '/contact/', class: '' },
]
const HamburgerButton = ({ onClick, hasLightBg }) => (
  <button
    onClick={onClick}
    className="hamburger-button focus:outline-none focus:ring-2 focus:ring-opacity-50 focus:ring-tuya-rho z-50 lg:z-0"
  >
    {[0, 1, 2].map(x => (
      <div key={x} className={`stripe ${hasLightBg ? 'bg-tuya-rho' : 'bg-white'}`} />
    ))}
  </button>
)

const MenuOpeningHours = ({ title, content }) => {
  const contentHours = content?.split(';')

  const singleLine = contentHours?.length === 1
  return (
    <p className={`text-white flex ${singleLine ? 'flex-row' : 'flex-col mt-2'} `}>
      <span className={`${singleLine ? 'mr-2' : ''} `}>{title}</span>
      {contentHours && (
        <p className={`text-white flex ${singleLine ? 'flex-row' : 'flex-col'} `}>
          {contentHours.map((item, index) => {
            return <span key={item + index}>{item}</span>
          })}
        </p>
      )}
    </p>
  )
}
const Menu = ({ originalPath, globalData, onClose, innerRef }) => {
  return (
    <motion.div
      ref={innerRef}
      initial={{ x: '-100%' }}
      animate={{
        x: 0,
        transition: {
          type: 'spring',
          stiffness: 300,
          damping: 40,
        },
      }}
      exit={{
        x: '-100%',
        transition: {
          type: 'spring',
          stiffness: 500,
          damping: 40,
        },
      }}
      transition
      key="menu-bar"
      style={{ maxWidth: 560 }}
      className="fixed h-screen w-full lg:w-3/5 xl:w-2/5 bg-tuya-psi left-0 top-0 z-40 flex justify-between items-center lg:items-start flex-col pt-40 pb-20 lg:py-16 lg:px-32 2xl:pt-[100px]"
    >
      <button onClick={onClose} className="absolute top-10 right-9 hidden lg:block">
        <CloseIcon />
      </button>
      <div>
        <ul className="text-3xl lg:text-4xl">
          {navMenu.map(item => {
            const textClass = item.path === originalPath ? 'text-white' : ' text-tuya-upsilon'
            return (
              <li className={`${textClass} hover:text-white transition-colors whitespace-nowrap`} key={item.title}>
                <Link to={item.path} className="leading-snug 2xl:leading-[54px] font-medium">
                  <Trans>{item.title}</Trans>
                </Link>
              </li>
            )
          })}
        </ul>
        <div className="hidden 2xl:grid grid-cols-4 gap-6 mt-7">
          <IconDomo />
          <IconCastle />
          <IconGaleria />
          <IconTeatro />
        </div>
      </div>

      <div className="hidden lg:block">
        <div className="flex flex-col text-[13px]	text-white mb-16 tracking-[0.5px] leading-[20px] font-normal">
          <span className="font-bold text-xxs text-tuya-upsilon mb-3 tracking-[2px]">
            <Trans>footer_locations</Trans>
          </span>
          <a href="https://tuya.ro/" className="mb-1" target="_blank" rel="noreferrer" title="Tuya Bucharest">
            Bucharest
          </a>
          <a href="https://www.tuya.gr/" target="_blank" rel="noreferrer" title="Tuya Mykonos">
            Mykonos
          </a>
        </div>
        <Pattern className="text-tuya-upsilon" />
      </div>
      <div className="items-start lg:hidden">
        <div className="opening-hours mb-8">
          <h6 className="text-tuya-delta mb-1 small-text-spaced">
            <Trans>opening_hours</Trans> oo
          </h6>
          {globalData.schedule1Title && globalData.schedule1Content && (
            <MenuOpeningHours title={globalData.schedule1Title} content={globalData.schedule1Content} />
          )}
          {globalData.schedule2Title && globalData.schedule2Content && (
            <MenuOpeningHours title={globalData.schedule2Title} content={globalData.schedule2Content} />
          )}
        </div>
        <a href="/reservation">
          <Button>
            <Trans>reservation</Trans>
          </Button>
        </a>
      </div>
    </motion.div>
  )
}

const OpeningHours = ({ title, content, className }) => {
  const contentHours = content?.split(';')
  const singleLine = contentHours?.length === 1
  return (
    <div className={`flex ${singleLine ? 'flex-row' : 'flex-col mt-2'} ${className}`}>
      <h6 className="text-tuya-delta  small-text-spaced uppercase mr-2">{title}</h6>
      {contentHours && (
        <p className="text-white flex flex-col">
          {contentHours.map((item, index) => {
            return <span key={item + index}>{item}</span>
          })}
        </p>
      )}
    </div>
  )
}
const Header = ({ globalData, hasLightBg, simpleHeader, alwaysBlack }) => {
  const { originalPath } = useI18next()
  const [isOpened, setIsOpened] = useState(false)
  const [isPassedNav, setIsPassedNav] = useState(false)
  const [isHalfPassedNav, setIsHalfPassedNav] = useState(false)
  const { scrollY } = useViewportScroll()
  const scrollDirection = useScrollDirection()
  const windowSize = useWindowSize()

  useEffect(
    () =>
      scrollY.onChange(v => {
        setIsPassedNav(v > 100)
        setIsHalfPassedNav(v > 37)
      }),
    [scrollY],
  )

  useEffect(() => {
    if (isOpened) {
      if (windowSize.width > 768) window.scrollTo(0, 0)
      disableBodyScroll(menuEL.current)
    } else clearAllBodyScrollLocks()
  }, [isOpened, windowSize])
  const menuEL = useRef(null)
  return (
    <>
      <motion.header
        initial={false}
        animate={
          scrollDirection === 'down' && windowSize.width < 768 && isHalfPassedNav
            ? {
                y: '-100%',
                transition: {
                  type: 'spring',
                  duration: 0.2,
                },
              }
            : {
                y: 0,
                transition: {
                  type: 'spring',
                  stiffness: 400,
                  damping: 40,
                },
              }
        }
        transition={{}}
        className={`bg-black w-full ${isPassedNav ? 'pt-8' : ''}
        ${alwaysBlack ? 'md:bg-opacity-100 ' : 'md:bg-opacity-30'}
        fixed md:absolute z-50 navbar flex items-center px-6 justify-between transition-colors`}
      >
        <HamburgerButton
          onClick={() => setIsOpened(old => !old)}
          hasLightBg={
            (hasLightBg && !isPassedNav && windowSize.width < 768) ||
            (simpleHeader && !alwaysBlack && windowSize.width > 767)
          }
        />
        <LanguageSwitcher />
        {!simpleHeader && (
          <div className="md:flex items-start hidden">
            <a href="/reservation">
              <Button>
                <Trans>reservation</Trans>
              </Button>
            </a>
            <div className="opening-hours ml-8 place-self-center flex flex-row items-center">
              <h6 className="text-tuya-delta mb-1 small-text-spaced mr-4 whitespace-nowrap">
                <Trans>opening</Trans>
                <br />
                <Trans>hours</Trans>
              </h6>
              <div className="flex flex-col">
                {globalData.schedule1Title && globalData.schedule1Content && (
                  <OpeningHours title={globalData.schedule1Title} content={globalData.schedule1Content} />
                )}
                {globalData.schedule2Title && globalData.schedule2Content && (
                  <OpeningHours title={globalData.schedule2Title} content={globalData.schedule2Content} />
                )}
              </div>
            </div>
          </div>
        )}

        <Link className="text-white z-50" to="/">
          <Logo
            className={`${
              (hasLightBg && !isPassedNav && windowSize.width < 768) ||
              (simpleHeader && !alwaysBlack && windowSize.width > 767)
                ? 'text-tuya-slate'
                : ''
            }`}
          />
        </Link>
        <AnimatePresence mode="wait">
          {isOpened && (
            <Menu
              originalPath={originalPath}
              globalData={globalData}
              onClose={() => setIsOpened(false)}
              innerRef={menuEL}
            />
          )}
        </AnimatePresence>
      </motion.header>
      <AnimatePresence mode="wait">
        {isOpened && (
          <motion.div
            onClick={() => setIsOpened(false)}
            initial={{ opacity: 0 }}
            animate={{ opacity: 0.5 }}
            exit={{ opacity: 0 }}
            key="overlay"
            className="bg-black h-screen w-full fixed top-0 left-0 z-40"
          />
        )}
      </AnimatePresence>
    </>
  )
}

Header.propTypes = {
  globalData: PropTypes.shape({
    address: PropTypes.string,
    email: PropTypes.string,
    facebookLink: PropTypes.string,
    instagramLink: PropTypes.string,
    phone: PropTypes.string,
    pinterestLink: PropTypes.string,
    locale: PropTypes.string,
    linkedinLink: PropTypes.string,
  }),
}

export default Header
